import { ElementRef, Injectable } from '@angular/core';

import { ScreenUtilsService } from 'core/services';
import { BehaviorSubject, Subject, distinctUntilChanged, filter, map, takeUntil } from 'rxjs';
import { PdfViewerService } from '../../../pdf-viewer.service';
import { PdfDocumentViewportsContainerService } from '../../pdf-document-viewports-container/pdf-document-viewports-container.service';

export type ZoomDef = { key: number | 'pageWidth', selectedDisplay: string, display: string }

@Injectable()
export class PdfZoomService {

  private readonly _destroy = new Subject<void>()

  private readonly numericZooms = [
    25,
    50,
    75,
    100,
    125,
    150,
    200,
    250,
    400,
    600,
  ] as const;

  private readonly customZooms: ZoomDef[] = [
    { key: 'pageWidth', selectedDisplay: '<==>', display: 'ss-pdf.page-width' } as const
  ]

  readonly allowedZooms: ZoomDef[] = [
    ...this.numericZooms.map<ZoomDef>((zoom) => ({ display: zoom + '%', key: zoom, selectedDisplay: zoom + '%' })),
    ...this.customZooms
  ]


  readonly selectedZoomDefKeySubject = new BehaviorSubject<number | 'pageWidth'>(100);

  readonly selectedZoomSubject = this._pdfViewerService.selectedZoomSubject;

  readonly screenSizeSubject = this._screenUtilsService.screenSizeSubject;


  readonly virtualScrollableElementSubject = this._pdfDocumentViewportsContainerService.virtualScrollableElementSubject;
  get virtualScrollableElement() {
    return this.virtualScrollableElementSubject.value;
  }

  get pageWidthZoom() {
    const containerRef = this.virtualScrollableElement.getElementRef() as ElementRef<HTMLDivElement>;

    return 1
  }


  readonly sidebarOpenedSubject = this._pdfViewerService.sidebarOpenedSubject;

  readonly drawerModeSubject = this._pdfViewerService.drawerModeSubject;

  constructor(
    private readonly _screenUtilsService: ScreenUtilsService,
    private readonly _pdfDocumentViewportsContainerService: PdfDocumentViewportsContainerService,
    private readonly _pdfViewerService: PdfViewerService
  ) {


    this._screenUtilsService.screenSizeSubject
      .pipe(
        map((screnSize) => screnSize === 'sm' || screnSize === 'xs'),
        distinctUntilChanged(),
        filter(Boolean),
        takeUntil(this._destroy)
      )
      .subscribe(() => {

        this.selectedZoomDefKeySubject.next('pageWidth');
      })


    this.selectedZoomDefKeySubject
      .pipe(
        distinctUntilChanged(),
        takeUntil(this._destroy)
      )
      .subscribe((value) => {


        if (value === 'pageWidth') {
          this._pdfViewerService.inPageWidthModeSubject.next(true);
          return;
        }
        this.selectedZoomSubject.next(value)

        this._pdfViewerService.inPageWidthModeSubject.next(false);
      })
  }

  zoomIn() {

    const selectedZoom = this.selectedZoomSubject.value;

    const nextZoom = this.numericZooms.find((zoom) => zoom > selectedZoom);
    if (!nextZoom) {
      return;
    }

    this.selectedZoomDefKeySubject.next(nextZoom);

  }

  zoomOut() {

    const selectedZoom = this.selectedZoomSubject.value;

    const prevZoom = [...this.numericZooms]
      .sort((a: number, b: number) => b - a)
      .find((zoom) => zoom < selectedZoom);

    if (!prevZoom) {
      return;
    }

    this.selectedZoomDefKeySubject.next(prevZoom);
  }

  setPageWidthZoom() {

    const pageWidthZoom = this.pageWidthZoom;
    this.selectedZoomSubject.next(pageWidthZoom);
  }

  ngOnDestroy() {

    this._destroy.next();
    this._destroy.complete();

  }
}
